/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { TrackingServiceV2 } from './feature-service-types';
import type { AppContent, FeatureAppProps } from '../../@types/interfaces';

import { EditorialText } from './components/EditorialText';
import { normalizeContent } from './components/normalizeContent';
import { useWltpComplianceGate } from './useWltpComplianceGate';

import mockContent from './content.json';
import { getNextId } from '../utils/getNextId';
import { apolloClient } from './constants';
import { ApolloProvider } from '@apollo/client';

import { detectPlacement } from '../utils/detectFaPlacement';
import type { PlacementResult } from '../utils/detectFaPlacement';
import { fetchCaeEnforcement } from './marketCompliance';

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const {
    contentService,
    trackingService: trackingServiceInherited,
    footnoteReferenceService,
    vueFormatterService,
    localeService,
    logger,
    dealerContextService,
    config,
    isMarketComplianceEnabled,
    initialWltpCompliance,
    initialWltpLogs,
    gfaServiceConfigProvider,
  } = inherited;

  const trackingService =
    typeof window !== 'undefined'
      ? trackingServiceInherited
      : (undefined as TrackingServiceV2 | undefined);

  const hasValidConfig = config && ('fields' in config || 'theme' in config);
  const [rawContent, setRawContent] = useState(
    hasValidConfig ? config : (contentService?.getContent() as AppContent),
  );

  const hasReplayedInitialWltpLogs = useRef(false);

  const [fetchedCompliance, setFetchedCompliance] = useState<boolean>(false);
  const complianceEnabled = isMarketComplianceEnabled ?? fetchedCompliance;

  useEffect(() => {
    if (hasReplayedInitialWltpLogs.current) return;
    if (!Array.isArray(initialWltpLogs) || !initialWltpLogs.length) return;

    initialWltpLogs.forEach((log) => {
      console.error(log);
    });

    hasReplayedInitialWltpLogs.current = true;
  }, [initialWltpLogs]);

  useEffect(() => {
    const featureAppId = trackingService?.featureAppId ?? getNextId(__FEATURE_APP_NAME__);

    if (!trackingService) {
      return;
    }

    trackingService.featureAppName = 'editorial-text';
    const additionalObjects = {
      componentInfo: {
        componentID: featureAppId,
      },
      attributes: {
        implementer: 2,
        version: __FEATURE_APP_VERSION__,
      },
    };

    const dataLayerEvent = {
      eventInfo: {
        eventAction: 'feature_app_ready',
        eventName: 'editorial text - feature app ready',
      },
      attributes: {
        componentName: 'editorial-text',
        label: '',
        targetURL: '',
        clickID: '',
        elementName: '',
        value: '',
        pos: '',
      },
    };
    trackingService.track({ event: dataLayerEvent, componentUpdate: additionalObjects });
  }, [trackingService]);

  useEffect(() => {
    const listener = () => {
      setRawContent(normalizeContent(contentService?.getContent() as AppContent));
    };

    contentService?.onContentChange(listener);

    return () => {
      contentService?.removeOnContentChange(listener);
    };
  }, [contentService, setRawContent]);

  const content = useMemo(() => normalizeContent(rawContent ?? mockContent), [rawContent]);

  // -------------------------------------------------------------------------
  //                            SITEWIDE SEARCH
  // -------------------------------------------------------------------------

  const [placement, setPlacement] = useState<PlacementResult | null>(null);
  const layoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const result = detectPlacement(layoutRef.current);
      setPlacement(result);
    } catch (error) {
      setPlacement(null);
      logger?.error(
        'Failed to detect Feature App placement.',
        { event: 'placement-detection-failed' },
        error,
      );
    }
  }, [logger]);

  useEffect(() => {
    if (isMarketComplianceEnabled !== undefined) return;

    let cancelled = false;
    fetchCaeEnforcement({ gfaServiceConfigProvider })
      .then((result) => {
        if (!cancelled) setFetchedCompliance(result.caeDataRequired);
      })
      .catch(() => {
        // Error fetching CAE enforcement
      });
    return () => {
      cancelled = true;
    };
  }, [gfaServiceConfigProvider, isMarketComplianceEnabled]);

  const isAcceptableMarket = localeService?.market === 'USA' || localeService?.market === 'CAN';
  const showSearch = isAcceptableMarket && content.enableSearch;

  logger?.info(placement);

  const isCompliant = useWltpComplianceGate({
    content,
    vueFormatterService,
    localeService,
    logger,
    isMarketComplianceEnabled: complianceEnabled,
    initialCompliance: initialWltpCompliance,
  });

  // -------------------------------------------------------------------------

  if (!isCompliant) {
    return null;
  }

  return (
    <ApolloProvider client={apolloClient}>
      <EditorialText
        content={content}
        footnoteReferenceService={footnoteReferenceService}
        vueFormatterService={vueFormatterService}
        localeService={localeService}
        trackingService={trackingService}
        dealerContextService={dealerContextService}
        showSearch={showSearch}
      />
    </ApolloProvider>
  );
};

export default FeatureApp;
